import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Typography,
  CircularProgress,
  Popper,
  ClickAwayListener,
} from '@mui/material';
import { Add as AddIcon, LocalOffer as TagIcon } from '@mui/icons-material';
import { tagService } from '../../services';
import type { UnifiedTag, CreateTagRequest } from '../../services';
import type { TagScope } from '../../types/content.types';

interface TagAutoCompleteProps {
  /** Currently applied tag IDs */
  appliedTagIds: string[];
  /** Available tags to search from */
  availableTags: UnifiedTag[];
  /** Loading state for fetching tags */
  loading?: boolean;
  /** Callback when a tag is selected or created */
  onTagApply: (tagId: string, tag: UnifiedTag) => void;
  /** Callback when a tag is removed */
  onTagRemove: (tagId: string) => void;
  /** Function to create a new tag */
  onCreateTag: (request: CreateTagRequest) => Promise<UnifiedTag>;
  /** Scope for new tags (personal, team, etc.) */
  tagScope: TagScope;
  /** Organization ID for new tags */
  organizationId: string;
  /** Team ID for new tags (required for all tags) */
  teamId: string;
  /** User ID for personal tags */
  userId: string;
  /** Placeholder text */
  placeholder?: string;
  /** Label for the input */
  label?: string;
  /** Maximum number of tags allowed */
  maxTags?: number;
}

const TagAutoComplete: React.FC<TagAutoCompleteProps> = ({
  appliedTagIds,
  availableTags,
  loading = false,
  onTagApply,
  onTagRemove,
  onCreateTag,
  tagScope,
  organizationId,
  teamId,
  userId,
  placeholder = "Type to add tags...",
  label = "Tags",
  maxTags = 20
}) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<UnifiedTag[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [creating, setCreating] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Get applied tags from available tags
  const appliedTags = availableTags.filter(tag => appliedTagIds.includes(tag.id));

  // Filter suggestions based on input
  useEffect(() => {
    const rawQuery = inputValue.toLowerCase().trim();
    
    // Handle '/' command to show all available tags
    if (rawQuery === '/' || rawQuery.startsWith('/ ')) {
      const searchQuery = rawQuery.startsWith('/ ') ? rawQuery.substring(2).trim() : '';
      
      let filtered = availableTags.filter(tag => {
        // Skip already applied tags
        if (appliedTagIds.includes(tag.id)) return false;
        
        // Skip tags without proper name
        if (!tag.name || typeof tag.name !== 'string') return false;
        
        // If no search query after '/', show all available tags
        if (!searchQuery) return true;
        
        // Otherwise filter by the search query (already stripped of '/')
        const nameMatch = tag.name.toLowerCase().includes(searchQuery);
        const descMatch = (tag.description && typeof tag.description === 'string') 
          ? tag.description.toLowerCase().includes(searchQuery) 
          : false;
        return nameMatch || descMatch;
      });
      
      // Sort by usage count (if available) for better discoverability
      filtered = filtered.sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0));
      
      setSuggestions(filtered.slice(0, 12)); // Show more when browsing all tags
      setShowSuggestions(true);
      return;
    }
    
    // Normal search behavior - strip leading '/' if present for backward compatibility
    const query = rawQuery.startsWith('/') ? rawQuery.substring(1).trim() : rawQuery;
    
    if (!query) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    // Find tags that match the input and aren't already applied
    const filtered = availableTags.filter(tag => {
      // Skip already applied tags
      if (appliedTagIds.includes(tag.id)) return false;
      
      // Skip tags without proper name
      if (!tag.name || typeof tag.name !== 'string') return false;
      
      // Match by name (case insensitive) using cleaned query
      const nameMatch = tag.name.toLowerCase().includes(query);
      
      // Match by description if available
      const descMatch = (tag.description && typeof tag.description === 'string')
        ? tag.description.toLowerCase().includes(query)
        : false;
      
      return nameMatch || descMatch;
    }).slice(0, 8); // Limit to 8 suggestions for normal search

    setSuggestions(filtered);
    setShowSuggestions(filtered.length > 0 || query.length > 0);
  }, [inputValue, availableTags, appliedTagIds]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleFocus = () => {
    setIsFocused(true);
    // Show suggestions if we have available tags but no input
    if (inputValue.trim() === '' && availableTags.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Delay hiding suggestions to allow clicking on them
    setTimeout(() => {
      if (!showSuggestions) {
        setShowSuggestions(false);
      }
    }, 150);
  };

  const handleSuggestionClick = (tag: UnifiedTag) => {
    onTagApply(tag.id, tag);
    setInputValue('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleCreateNewTag = async () => {
    const rawTagName = inputValue.trim();
    // Strip leading '/' if present for tag creation
    const tagName = rawTagName.startsWith('/') ? rawTagName.substring(1).trim() : rawTagName;
    
    if (!tagName || creating) return;

    // Check if tag already exists (case insensitive)
    const existingTag = availableTags.find(tag => 
      tag.name.toLowerCase() === tagName.toLowerCase()
    );

    if (existingTag) {
      // Use existing tag if found
      handleSuggestionClick(existingTag);
      return;
    }

    try {
      setCreating(true);
      
      const createRequest: CreateTagRequest = {
        name: tagName,
        description: `${tagScope === 'personal' ? 'Personal' : 'Team'} tag for organizing content`,
        color: ['#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#f44336', '#00bcd4', '#8bc34a'][Math.floor(Math.random() * 7)],
        scope: tagScope,
        owner_id: tagScope === 'personal' ? userId : teamId,
        organization_id: organizationId,
        team_id: teamId  // Always required now
      };

      const newTag = await onCreateTag(createRequest);
      
      // Handle different response structures - the API might return nested data
      const tagId = newTag.id || (newTag as any).tag?.id || (newTag as any).data?.tag?.id;
      const tagData = (newTag as any).tag || (newTag as any).data?.tag || newTag;
      
      if (!tagId) {
        console.error('No tag ID found in response:', newTag);
        throw new Error('Failed to get tag ID from created tag');
      }
      
      console.log('Applying newly created tag with ID:', tagId);
      onTagApply(tagId, tagData);
      
      setInputValue('');
      setShowSuggestions(false);
      inputRef.current?.focus();
    } catch (error) {
      console.error('Failed to create tag:', error);
      // Error handling is done in the parent component
    } finally {
      setCreating(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && inputValue.trim()) {
      event.preventDefault();
      
      const rawQuery = inputValue.toLowerCase().trim();
      
      // Handle '/' command - don't create a tag called '/'
      if (rawQuery === '/') {
        // Just show the suggestions, don't create anything
        return;
      }
      
      // If it's a search after '/', find matches
      if (rawQuery.startsWith('/ ')) {
        const searchQuery = rawQuery.substring(2).trim();
        if (searchQuery && suggestions.length > 0) {
          // Use the first suggestion
          handleSuggestionClick(suggestions[0]);
        }
        return;
      }
      
      // Strip leading '/' for exact match comparison
      const cleanQuery = rawQuery.startsWith('/') ? rawQuery.substring(1).trim() : rawQuery;
      
      // If there's an exact match in suggestions, use it (compare with cleaned query)
      const exactMatch = suggestions.find(tag => 
        tag.name.toLowerCase() === cleanQuery
      );
      
      if (exactMatch) {
        handleSuggestionClick(exactMatch);
      } else {
        // handleCreateNewTag will handle stripping the '/' if needed
        handleCreateNewTag();
      }
    } else if (event.key === 'Escape') {
      setShowSuggestions(false);
      inputRef.current?.blur();
    }
  };

  const handleRemoveTag = (tagId: string) => {
    onTagRemove(tagId);
  };

  const handleClickAway = () => {
    setShowSuggestions(false);
  };

  const canAddMoreTags = appliedTags.length < maxTags;

  return (
    <Box>
      {/* Applied Tags Display */}
      {appliedTags.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Applied {label}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {appliedTags.map(tag => (
              <Chip
                key={tag.id}
                label={tag.name}
                size="small"
                onDelete={() => handleRemoveTag(tag.id)}
                deleteIcon={<TagIcon />}
                sx={{
                  backgroundColor: tag.color ? tag.color + '20' : '#2196f3' + '20',
                  borderColor: tag.color || '#2196f3',
                  color: '#000000',
                }}
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Tag Input */}
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box ref={anchorRef} sx={{ position: 'relative' }}>
          <TextField
            ref={inputRef}
            fullWidth
            size="small"
            label={canAddMoreTags ? label : `${label} (${maxTags} max)`}
            placeholder={canAddMoreTags ? placeholder : `Maximum ${maxTags} tags reached`}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={!canAddMoreTags}
            InputProps={{
              endAdornment: loading ? <CircularProgress size={20} /> : null,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: showSuggestions ? 'primary.main' : undefined,
                },
              },
            }}
          />

          {/* Suggestions Popper */}
          <Popper
            open={showSuggestions && (suggestions.length > 0 || inputValue.trim().length > 0 || (isFocused && inputValue.trim() === '' && availableTags.length > 0))}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
          >
            <Paper elevation={3} sx={{ mt: 0.5, maxHeight: 300, overflow: 'auto' }}>
              <List dense>
                {/* Existing tag suggestions */}
                {suggestions.map(tag => (
                  <ListItem key={tag.id} disablePadding>
                    <ListItemButton onClick={() => handleSuggestionClick(tag)}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: tag.color || '#2196f3',
                            flexShrink: 0
                          }}
                        />
                        <ListItemText
                          primary={tag.name}
                          secondary={tag.description}
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            sx: { fontWeight: 500 }
                          }}
                          secondaryTypographyProps={{ 
                            variant: 'caption',
                            sx: { 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical'
                            }
                          }}
                        />
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}

                {/* Show helper text for '/' command when focused but no input */}
                {isFocused && inputValue.trim() === '' && availableTags.length > 0 && (
                  <ListItem>
                    <ListItemText
                      primary="Type '/' to browse all available tags, or start typing to search"
                      primaryTypographyProps={{
                        variant: 'caption',
                        sx: { fontStyle: 'italic', color: 'text.secondary' }
                      }}
                    />
                  </ListItem>
                )}

                {/* Show enhanced info when using '/' command */}
                {inputValue.trim() === '/' && suggestions.length > 0 && (
                  <ListItem>
                    <ListItemText
                      primary={`Showing ${suggestions.length} available system tags`}
                      secondary="Continue typing to filter, or select from the list below"
                      primaryTypographyProps={{
                        variant: 'caption',
                        sx: { fontWeight: 600, color: 'primary.main' }
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        sx: { color: 'text.secondary' }
                      }}
                    />
                  </ListItem>
                )}

                {/* Create new tag option */}
                {inputValue.trim() && (
                  <ListItem disablePadding>
                    <ListItemButton 
                      onClick={handleCreateNewTag}
                      disabled={creating}
                      sx={{ 
                        borderTop: suggestions.length > 0 ? '1px solid' : undefined,
                        borderColor: 'divider'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        {creating ? (
                          <CircularProgress size={16} />
                        ) : (
                          <AddIcon fontSize="small" color="primary" />
                        )}
                        <ListItemText
                          primary={`Create "${inputValue.trim()}"`}
                          primaryTypographyProps={{
                            variant: 'body2',
                            color: 'primary.main',
                            sx: { fontWeight: 500 }
                          }}
                        />
                      </Box>
                    </ListItemButton>
                  </ListItem>
                )}

                {/* No suggestions message */}
                {suggestions.length === 0 && !inputValue.trim() && (
                  <ListItem>
                    <ListItemText
                      primary="Start typing to search or create tags"
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: 'text.secondary',
                        align: 'center'
                      }}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Popper>
        </Box>
      </ClickAwayListener>
    </Box>
  );
};

export default TagAutoComplete;